import React, { useState, useEffect } from 'react';
import { Modal, Form, Input } from 'antd';

/**
 * Prompt the user for the 4-digit PIN that protects a corporate group.
 *
 * The modal does not call the backend on its own — it just collects the PIN
 * and hands it back via onConfirm. The parent submits it together with the
 * property-settings update so the backend can verify and apply the change
 * atomically.
 */
const CorporateGroupPinModal = ({ visible, groupName, confirmLoading, onCancel, onConfirm }) => {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (visible) {
      form.resetFields();
    }
  }, [visible, form]);

  const handleOk = () => {
    form
      .validateFields()
      .then(({ pin }) => {
        setSubmitting(true);
        Promise.resolve(onConfirm(pin)).finally(() => setSubmitting(false));
      })
      .catch(() => {});
  };

  return (
    <Modal
      visible={visible}
      title={groupName ? `Enter PIN for ${groupName}` : 'Enter Corporate Group PIN'}
      okText="Verify & Save"
      cancelText="Cancel"
      onOk={handleOk}
      onCancel={onCancel}
      confirmLoading={confirmLoading || submitting}
      destroyOnClose
      maskClosable={false}
    >
      <p style={{ marginTop: 0, color: '#555' }}>
        This corporate group is protected by a 4-digit PIN. Enter it to confirm
        the assignment.
      </p>
      <Form form={form} layout="vertical">
        <Form.Item
          name="pin"
          label="4-digit PIN"
          rules={[
            { required: true, message: 'PIN is required' },
            {
              validator: (_, value) => {
                if (!value) return Promise.resolve();
                if (!/^\d{4}$/.test(value)) {
                  return Promise.reject(new Error('PIN must be exactly 4 digits'));
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          <Input
            size="large"
            maxLength={4}
            inputMode="numeric"
            type="password"
            placeholder="••••"
            autoFocus
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default CorporateGroupPinModal;
